import { useState, useRef, useEffect } from "react";
import { flushSync } from "react-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, ImageIcon, Eye, ChevronUp, ChevronDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  buildUnitCheckWalkthrough,
  reindexProofStandardsFromWalkthrough,
  type UnitCheckRoomSection,
} from "@/lib/proofStandardsDisplay";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  useProofStandards,
  useCreateProofStandard,
  useUpdateProofStandard,
  useDeleteProofStandard,
  RECOMMENDED_STANDARDS,
  type ProofStandard,
  type ProofStandardType,
  type ProofStandardPriority,
} from "@/hooks/useProofStandards";
import { isLikelyImageFile, prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { normalizeYoutubeReferenceUrl } from "@/lib/youtubeReference";

const STANDARD_TYPES: { value: ProofStandardType; label: string }[] = [
  { value: "proof_photo", label: "Photo" },
  { value: "proof_video", label: "Video" },
  { value: "functional_check", label: "Functional check" },
];

const PRIORITIES: { value: ProofStandardPriority; label: string }[] = [
  { value: "normal", label: "Normal" },
  { value: "high_value", label: "High value (recommended)" },
];

interface StandardsAndProofSectionProps {
  propertyId: string;
  checklistId: string | null;
  roomNames: string[];
  /** Overrides default "Unit Items" heading (checklist builder tab label). */
  sectionTitle?: string;
  /** Overrides default intro copy under the heading. */
  sectionDescription?: string;
  /** Empty list helper text (optional). */
  listEmptyHint?: string;
}

const DEFAULT_SECTION_TITLE = "Photo list";
const DEFAULT_SECTION_DESCRIPTION =
  "Shots cleaners take before and after every clean. Add a title for each thing you need photo proof of.";

function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm|mov)(\?|$)/i.test(url);
}

type PendingReferenceMedia = {
  id: string;
  file: File;
  previewUrl: string;
};

function isVideoFile(file: File): boolean {
  return (file.type || "").startsWith("video/");
}

/** Blob preview with fallback when the browser cannot decode (e.g. HEIC). */
function ReferenceFilePreview({ url, fileName }: { url: string; fileName: string }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return (
      <div className="flex h-24 flex-col items-center justify-center gap-1 px-1 text-muted-foreground">
        <ImageIcon className="h-6 w-6 shrink-0 opacity-60" />
        <span className="text-[10px] text-center line-clamp-2">{fileName}</span>
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      className="h-24 w-full object-cover"
      onError={() => setBroken(true)}
    />
  );
}

export default function StandardsAndProofSection({
  propertyId,
  checklistId,
  roomNames,
  sectionTitle,
  sectionDescription,
  listEmptyHint,
}: StandardsAndProofSectionProps) {
  const { toast } = useToast();
  const { data: standards = [], isLoading } = useProofStandards(checklistId ?? undefined);
  const createStandard = useCreateProofStandard();
  const updateStandard = useUpdateProofStandard();
  const deleteStandard = useDeleteProofStandard();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStandard, setEditingStandard] = useState<ProofStandard | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<ProofStandardType>("proof_photo");
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [priority, setPriority] = useState<ProofStandardPriority>("normal");
  const [cleanerInstructions, setCleanerInstructions] = useState("");
  const [referenceYoutubeInput, setReferenceYoutubeInput] = useState("");
  const [pendingReferenceMedia, setPendingReferenceMedia] = useState<PendingReferenceMedia[]>([]);
  /** Saved URLs still attached to this item (edit mode); user can remove before save. */
  const [keptReferenceUrls, setKeptReferenceUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** While the OS file picker is open, Radix may fire onOpenChange(false) — never reset then. */
  const filePickerOpenRef = useRef(false);

  useEffect(() => {
    const onWindowFocus = () => {
      window.setTimeout(() => {
        filePickerOpenRef.current = false;
      }, 400);
    };
    window.addEventListener("focus", onWindowFocus);
    return () => window.removeEventListener("focus", onWindowFocus);
  }, []);

  const closeDialog = () => {
    filePickerOpenRef.current = false;
    setDialogOpen(false);
    resetForm();
  };

  const clearPendingReferenceMedia = () => {
    pendingReferenceMedia.forEach((m) => URL.revokeObjectURL(m.previewUrl));
    setPendingReferenceMedia([]);
  };

  const resetForm = () => {
    setEditingStandard(null);
    setTitle("");
    setType("proof_photo");
    setSelectedRooms([]);
    setPriority("normal");
    setCleanerInstructions("");
    setReferenceYoutubeInput("");
    clearPendingReferenceMedia();
    setKeptReferenceUrls([]);
  };

  const openAdd = () => {
    resetForm();
    if (roomNames.length > 0) setSelectedRooms([roomNames[0]!]);
    setDialogOpen(true);
  };

  const openEdit = (s: ProofStandard) => {
    setEditingStandard(s);
    setTitle(s.title);
    setType(s.type as ProofStandardType);
    setSelectedRooms(
      s.rooms?.length ? [...s.rooms] : s.room?.trim() ? [s.room.trim()] : [],
    );
    setPriority(s.priority as ProofStandardPriority);
    setCleanerInstructions(s.cleaner_instructions ?? "");
    setReferenceYoutubeInput(s.reference_youtube_url?.trim() ?? "");
    clearPendingReferenceMedia();
    setKeptReferenceUrls([...(s.reference_media_urls ?? [])]);
    setDialogOpen(true);
  };

  const handleAddReferenceFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    filePickerOpenRef.current = false;
    // Reset input so the same file can be chosen again; read FileList first.
    e.target.value = "";
    if (!fileList?.length) return;

    const added: PendingReferenceMedia[] = [];
    for (const file of Array.from(fileList)) {
      let previewUrl = "";
      try {
        previewUrl = URL.createObjectURL(file);
      } catch {
        previewUrl = "";
      }
      added.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random()}`,
        file,
        previewUrl,
      });
    }

    flushSync(() => {
      setPendingReferenceMedia((prev) => [...prev, ...added]);
    });
  };

  const openReferenceFilePicker = () => {
    filePickerOpenRef.current = true;
    fileInputRef.current?.click();
  };

  const removePendingReferenceMedia = (id: string) => {
    setPendingReferenceMedia((prev) => {
      const removed = prev.find((m) => m.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((m) => m.id !== id);
    });
  };

  const uploadReferenceMedia = async (
    standardId: string,
    files: File[]
  ): Promise<string[]> => {
    const urls: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const raw = files[i];
      const file =
        (raw.type || "").startsWith("video/") || !isLikelyImageFile(raw)
          ? raw
          : await prepareImageFileForUpload(raw);
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 40);
      const path = `${propertyId}/proof-standards/${standardId}/${Date.now()}_${i}_${safeName}`;
      const { error } = await supabase.storage
        .from("reference-photos")
        .upload(path, file, {
          contentType: file.type || (file.name.match(/\.(mp4|webm|mov)$/i) ? "video/mp4" : "image/jpeg"),
          upsert: true,
        });
      if (error) throw error;
      const { data } = supabase.storage.from("reference-photos").getPublicUrl(path);
      urls.push(data.publicUrl);
    }
    return urls;
  };

  const handleSave = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    if (!checklistId) return;

    const normalizedYoutube = normalizeYoutubeReferenceUrl(referenceYoutubeInput);
    if (referenceYoutubeInput.trim() && !normalizedYoutube) {
      toast({
        title: "Invalid YouTube URL",
        description: "Use a full YouTube watch, Shorts, embed, or youtu.be link.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingStandard) {
        const newUrls =
          pendingReferenceMedia.length > 0
            ? await uploadReferenceMedia(
                editingStandard.id,
                pendingReferenceMedia.map((m) => m.file)
              )
            : [];
        await updateStandard.mutateAsync({
          id: editingStandard.id,
          checklist_id: checklistId,
          title: trimmedTitle,
          type,
          rooms: selectedRooms,
          priority,
          cleaner_instructions: cleanerInstructions.trim() || null,
          required_every_turnover: true,
          reference_media_urls: [...keptReferenceUrls, ...newUrls],
          reference_youtube_url: normalizedYoutube,
        });
        toast({ title: "Item updated" });
      } else {
        const data = await createStandard.mutateAsync({
          property_id: propertyId,
          checklist_id: checklistId,
          title: trimmedTitle,
          type,
          rooms: selectedRooms,
          priority,
          cleaner_instructions: cleanerInstructions.trim() || null,
          required_every_turnover: true,
          display_order: standards.length,
          reference_youtube_url: normalizedYoutube,
        });
        if (pendingReferenceMedia.length > 0) {
          const urls = await uploadReferenceMedia(
            data.id,
            pendingReferenceMedia.map((m) => m.file)
          );
          await updateStandard.mutateAsync({
            id: data.id,
            checklist_id: checklistId,
            reference_media_urls: urls,
          });
        }
        toast({ title: "Item added" });
      }
      closeDialog();
    } catch (e) {
      toast({
        title: editingStandard ? "Failed to update item" : "Failed to add item",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (s: ProofStandard) => {
    if (!checklistId) return;
    try {
      await deleteStandard.mutateAsync({ id: s.id, checklist_id: checklistId });
      toast({ title: "Item removed" });
    } catch (e) {
      toast({ title: "Failed to remove", description: (e as Error).message, variant: "destructive" });
    }
  };

  const GENERAL_LABEL = "General";
  const walkthroughSections = buildUnitCheckWalkthrough(standards, roomNames, GENERAL_LABEL);

  const swapWalkthroughItems = (
    sections: UnitCheckRoomSection[],
    from: { sectionIndex: number; itemIndex: number },
    to: { sectionIndex: number; itemIndex: number },
  ): UnitCheckRoomSection[] => {
    const next = sections.map((s) => ({ label: s.label, items: [...s.items] }));
    const a = next[from.sectionIndex]!.items[from.itemIndex]!;
    const b = next[to.sectionIndex]!.items[to.itemIndex]!;
    next[from.sectionIndex]!.items[from.itemIndex] = b;
    next[to.sectionIndex]!.items[to.itemIndex] = a;
    return next;
  };

  const moveStandardInWalkthrough = async (
    sectionIndex: number,
    itemIndex: number,
    direction: "up" | "down",
  ) => {
    if (!checklistId) return;
    const sections = walkthroughSections.map((s) => ({ label: s.label, items: [...s.items] }));
    let targetSection = sectionIndex;
    let targetItem = itemIndex;

    if (direction === "up") {
      if (itemIndex > 0) targetItem = itemIndex - 1;
      else if (sectionIndex > 0) {
        targetSection = sectionIndex - 1;
        targetItem = sections[targetSection]!.items.length - 1;
      } else return;
    } else if (itemIndex < sections[sectionIndex]!.items.length - 1) {
      targetItem = itemIndex + 1;
    } else if (sectionIndex < sections.length - 1) {
      targetSection = sectionIndex + 1;
      targetItem = 0;
    } else return;

    const reordered = swapWalkthroughItems(
      sections,
      { sectionIndex, itemIndex },
      { sectionIndex: targetSection, itemIndex: targetItem },
    );
    const updates = reindexProofStandardsFromWalkthrough(reordered);
    try {
      await Promise.all(
        updates.map((u) =>
          updateStandard.mutateAsync({
            id: u.id,
            checklist_id: checklistId,
            display_order: u.display_order,
          }),
        ),
      );
    } catch (e) {
      toast({
        title: "Failed to reorder",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  };

  const isFirstWalkthroughItem = (sectionIndex: number, itemIndex: number) =>
    sectionIndex === 0 && itemIndex === 0;

  const isLastWalkthroughItem = (sectionIndex: number, itemIndex: number) => {
    const lastSection = walkthroughSections[walkthroughSections.length - 1];
    if (!lastSection) return true;
    return (
      sectionIndex === walkthroughSections.length - 1 &&
      itemIndex === lastSection.items.length - 1
    );
  };

  const handleAddRecommended = async () => {
    if (!checklistId) return;
    try {
      let order = standards.length;
      for (const rec of RECOMMENDED_STANDARDS) {
        await createStandard.mutateAsync({
          property_id: propertyId,
          checklist_id: checklistId,
          title: rec.title,
          type: rec.type,
          room: null,
          priority: rec.priority,
          cleaner_instructions: null,
          required_every_turnover: true,
          display_order: order++,
        });
      }
      toast({ title: "3 recommended Unit Check items added. Each requires cleaner proof every turnover." });
    } catch (e) {
      toast({ title: "Failed to add recommended", description: (e as Error).message, variant: "destructive" });
    }
  };

  const heading = sectionTitle ?? DEFAULT_SECTION_TITLE;
  const intro = sectionDescription ?? DEFAULT_SECTION_DESCRIPTION;
  const emptyHint =
    listEmptyHint ?? "No Unit Check items yet. Add one or use the 3-item recommended starter pack above.";

  if (!checklistId) return null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-4 w-4" />
            {heading}
          </CardTitle>
          <CardDescription>{intro}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={openAdd} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Unit Item
            </Button>
            <Button variant="outline" size="sm" onClick={handleAddRecommended} disabled={createStandard.isPending}>
              {createStandard.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add recommended starter pack (3)
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : standards.length === 0 ? (
            <p className="text-sm text-muted-foreground">{emptyHint}</p>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Cleaners walk room by room in the same order as your checklist Rooms tab — put the entry room first.
                Reorder items within and across rooms using the arrows.
              </p>
              {walkthroughSections.map((section, sectionIndex) => (
                <div key={section.label}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {section.label}
                    {sectionIndex === 0 && roomNames.length > 0 ? (
                      <span className="ml-2 font-normal normal-case text-primary">(starts here)</span>
                    ) : null}
                  </p>
                  <ul className="space-y-2">
                    {section.items.map((s, itemIndex) => (
                      <li
                        key={s.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm"
                      >
                        <div className="flex min-w-0 flex-1 items-start gap-2">
                          <div className="flex shrink-0 flex-col gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              disabled={
                                isFirstWalkthroughItem(sectionIndex, itemIndex) || updateStandard.isPending
                              }
                              onClick={() =>
                                void moveStandardInWalkthrough(sectionIndex, itemIndex, "up")
                              }
                              aria-label="Move up"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              disabled={
                                isLastWalkthroughItem(sectionIndex, itemIndex) || updateStandard.isPending
                              }
                              onClick={() =>
                                void moveStandardInWalkthrough(sectionIndex, itemIndex, "down")
                              }
                              aria-label="Move down"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="font-medium">{s.title}</span>
                            {(s.rooms?.length ?? 0) > 1 ? (
                              <p className="mt-0.5 text-xs text-primary">
                                Required in {s.rooms.join(", ")}
                              </p>
                            ) : null}
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <span className="text-xs capitalize text-muted-foreground">
                                {s.type === "proof_photo"
                                  ? "Proof Photo"
                                  : s.type === "proof_video"
                                    ? "Proof Video"
                                    : "Functional check"}
                              </span>
                              <span className="text-xs text-muted-foreground">·</span>
                              <span className="text-xs text-muted-foreground">
                                {s.priority === "high_value" ? "High value" : "Normal"}
                              </span>
                              {s.required_every_turnover ? (
                                <>
                                  <span className="text-xs text-muted-foreground">·</span>
                                  <span className="text-xs text-primary">Proof required</span>
                                </>
                              ) : null}
                            </div>
                            {(s.reference_media_urls?.length ?? 0) > 0 || s.reference_youtube_url ? (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {(s.reference_media_urls?.length ?? 0) > 0
                                  ? `${s.reference_media_urls!.length} reference file(s)`
                                  : null}
                                {(s.reference_media_urls?.length ?? 0) > 0 && s.reference_youtube_url ? " · " : null}
                                {s.reference_youtube_url ? "YouTube demo" : null}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>
                            <Eye className="mr-1 h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(s)}
                            disabled={deleteStandard.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setDialogOpen(true);
            return;
          }
          // File picker blur triggers dismiss — keep dialog open and do not reset form.
          if (filePickerOpenRef.current) {
            setDialogOpen(true);
            return;
          }
          setDialogOpen(false);
        }}
      >
        <DialogContent
          className="max-w-lg max-h-[90vh] overflow-y-auto [&>button.absolute]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            closeDialog();
          }}
        >
          <DialogHeader>
            <DialogTitle>{editingStandard ? "Edit Items" : "Add item"}</DialogTitle>
            <DialogDescription>
              Cleaners see uploaded reference files, an optional YouTube demo link, and your instructions. Proof upload is required every turnover (shown as &quot;Proof required&quot; on the task).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Living room decor, TV working + sound"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as ProofStandardType)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STANDARD_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rooms</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Select every room where the cleaner must complete this same check. Leave empty for General.
              </p>
              {roomNames.length === 0 ? (
                <p className="text-sm text-muted-foreground">Add rooms on the Rooms tab first.</p>
              ) : (
                <div className="space-y-2 rounded-lg border p-3">
                  {roomNames.map((r) => {
                    const checked = selectedRooms.includes(r);
                    return (
                      <label key={r} className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(next) => {
                            setSelectedRooms((prev) =>
                              next === true
                                ? [...prev, r]
                                : prev.filter((name) => name !== r),
                            );
                          }}
                        />
                        <span>{r}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as ProofStandardPriority)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Host demo — YouTube (optional)</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Paste a full YouTube link so cleaners can watch how you want it done. They still upload their own proof photo or video for this turnover.
              </p>
              <Input
                value={referenceYoutubeInput}
                onChange={(e) => setReferenceYoutubeInput(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=… or youtu.be/…"
                className="mt-1"
                inputMode="url"
                autoComplete="off"
              />
            </div>
            <div>
              <Label>Reference media</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Show the cleaner what &quot;correct&quot; looks like. Upload photo(s) and/or video files here (separate from YouTube).
              </p>
              {keptReferenceUrls.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {keptReferenceUrls.map((url, i) => (
                    <div
                      key={`${url}-${i}`}
                      className="relative w-28 shrink-0 overflow-hidden rounded-md border bg-muted/40"
                    >
                      {isVideoUrl(url) ? (
                        <video
                          src={url}
                          muted
                          playsInline
                          preload="metadata"
                          className="h-24 w-full object-cover bg-black/5"
                        />
                      ) : (
                        <img
                          src={url}
                          alt={`Reference ${i + 1}`}
                          className="h-24 w-full object-cover"
                        />
                      )}
                      <button
                        type="button"
                        aria-label={`Remove reference ${i + 1}`}
                        className="absolute right-1 top-1 rounded bg-background/90 p-1 text-destructive shadow-sm hover:bg-background"
                        onClick={() =>
                          setKeptReferenceUrls((prev) => prev.filter((_, idx) => idx !== i))
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <div className="flex items-center justify-between gap-1 border-t bg-background/95 px-1 py-0.5">
                        <span className="truncate text-[10px] text-muted-foreground">Ref {i + 1}</span>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-[10px] font-medium text-primary hover:underline"
                        >
                          Open
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,.heic,.heif,.HEIC,.HEIF"
                multiple
                className="hidden"
                onChange={handleAddReferenceFiles}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openReferenceFilePicker}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add photo/video
              </Button>
              {pendingReferenceMedia.length > 0 && (
                <p className="text-xs font-medium text-foreground mt-2">
                  {pendingReferenceMedia.length} file{pendingReferenceMedia.length !== 1 ? "s" : ""} selected — saves when you click Save
                </p>
              )}
              {pendingReferenceMedia.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-2">
                  {pendingReferenceMedia.map((item) => (
                    <div
                      key={item.id}
                      className="relative w-28 shrink-0 overflow-hidden rounded-md border bg-muted/40"
                    >
                      {item.previewUrl && isVideoFile(item.file) ? (
                        <video
                          src={item.previewUrl}
                          muted
                          playsInline
                          className="h-24 w-full object-cover"
                        />
                      ) : item.previewUrl ? (
                        <ReferenceFilePreview url={item.previewUrl} fileName={item.file.name} />
                      ) : (
                        <div className="flex h-24 flex-col items-center justify-center gap-1 px-1 text-muted-foreground">
                          <ImageIcon className="h-6 w-6 shrink-0 opacity-60" />
                          <span className="text-[10px] text-center line-clamp-2">{item.file.name}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        aria-label="Remove new upload"
                        className="absolute right-1 top-1 rounded bg-background/90 p-1 text-destructive shadow-sm hover:bg-background"
                        onClick={() => removePendingReferenceMedia(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <span className="block truncate px-1 py-0.5 text-center text-[10px] text-muted-foreground">
                        New
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Cleaner instructions (short)</Label>
              <Textarea
                value={cleanerInstructions}
                onChange={(e) => setCleanerInstructions(e.target.value)}
                placeholder="e.g. Record a 10s video showing TV turns on, sound works, and speaker is present."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={createStandard.isPending || updateStandard.isPending}>
              {(createStandard.isPending || updateStandard.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingStandard ? "Save changes" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
